// Pure helpers for the Comms Brief / Work Order editor (pages/ProgramDocumentEditor.js).
// The backend owns the section schema (app/documents/schema.py) and the content shape
// {section: {field: value}}; nothing here knows what any particular field means.

// Where an auto-filled value came from (backend provenance), as the editor labels it.
// 'default' is wording the app suggested because the real input isn't known yet; it is the
// one source that asks the PM to look again before the document goes out.
export const SOURCE_LABELS = {
  io: 'From IO',
  program: 'From program',
  qa_rules: 'From QA rules',
  contacts: 'From client contacts',
  roster: 'From team roster',
  brief: 'From comms brief',
  provider: 'From ContentIQ',
  default: 'Suggested, confirm',
  typed: 'Edited',
};

export const sourceLabel = (source) => SOURCE_LABELS[source] || '';
export const needsConfirm = (source) => source === 'default';

export const DOC_TYPE_LABELS = { comms_brief: 'Comms Brief', work_order: 'Work Order' };

export function blankValue(field) {
  switch (field.kind) {
    case 'lines': case 'table': case 'groups': return [];
    default: return '';
  }
}

export function blankItem(fields) {
  const item = {};
  fields.forEach(f => { item[f.key] = blankValue(f); });
  return item;
}

export const getValue = (content, section, key) => {
  const v = ((content || {})[section] || {})[key];
  return v === undefined ? undefined : v;
};

// Immutable single-field update.
export function setValue(content, section, key, value) {
  return { ...content, [section]: { ...((content || {})[section] || {}), [key]: value } };
}

export const linesToText = (lines) => (Array.isArray(lines) ? lines.join('\n') : '');
export const textToLines = (text) => String(text ?? '').split('\n');

// "Segment: Enterprise" / "Segment 2" for a repeated block.
export function groupHeading(field, item, index) {
  const name = ((item && (item.name || item.tier || item.product)) || '').trim();
  const label = field.item_label || 'Item';
  return name ? `${label}: ${name}` : `${label} ${index + 1}`;
}

// Fields still holding suggested wording, per section, so the editor can say "3 to confirm".
export function confirmCounts(schema, content, provenance) {
  const out = {};
  (schema?.sections || []).forEach(s => {
    out[s.key] = s.fields.filter(f => {
      const v = getValue(content, s.key, f.key);
      const filled = Array.isArray(v) ? v.length > 0 : String(v ?? '').trim() !== '';
      return filled && needsConfirm(((provenance || {})[s.key] || {})[f.key]);
    }).length;
  });
  return out;
}

export const isDirty = (content, saved) => JSON.stringify(content) !== JSON.stringify(saved);

export function exportFileName(docType, programName, version, ext) {
  const base = `${DOC_TYPE_LABELS[docType] || 'Document'} - ${programName || 'Program'} v${version}`;
  return `${base.replace(/[\\/:*?"<>|\r\n]+/g, '').trim()}.${ext}`;
}

// What the editor tells the PM after reading an edited Word file back: a headline plus the changed
// field labels and any parts of the file that could not be placed. Nothing is saved at this point.
export function summarizeImport(fileName, changed, warnings) {
  const n = (changed || []).length;
  const headline = n === 0
    ? `No differences found in ${fileName}.`
    : `Loaded ${n} change${n === 1 ? '' : 's'} from ${fileName}. Review them below, then Save.`;
  return { headline, changed: (changed || []).map(c => c.label), warnings: warnings || [] };
}

// One document can cover several programs: the first program owns it, the others only add their components
// and goals. The backend accepts up to four extra programs.
export const MAX_EXTRA_PROGRAMS = 4;

// The extra-program ids after picking `picked`: no repeats, never the owning program, capped.
export function addExtraProgram(extraIds, primaryId, picked) {
  const id = String(picked);
  if (!id || id === String(primaryId) || extraIds.includes(id) || extraIds.length >= MAX_EXTRA_PROGRAMS) return extraIds;
  return [...extraIds, id];
}
